import React from 'react';
import NavigationBar from '@/components/NavigationBar';
import { PRIVACY_VERSION } from './policyVersions';

const sectionClass = 'mb-7';
const headingClass = 'mb-3 text-2xl font-semibold text-gray-900 dark:text-gray-100';
const textClass = 'mb-3 text-gray-700 dark:text-gray-300';
const listClass = 'mb-3 list-disc space-y-2 pl-6 text-gray-700 dark:text-gray-300';

const Privacy = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
    <NavigationBar />
    <main
      data-policy-document="privacy"
      data-policy-version={PRIVACY_VERSION}
      className="mx-auto max-w-4xl p-6 pt-16"
    >
      <h1 className="mb-3 text-center text-4xl font-bold text-gray-900 dark:text-gray-100">
        Pinngro Human Beta Privacy Policy
      </h1>
      <p className="mb-8 text-center text-gray-700 dark:text-gray-300">
        Last updated: September 2, 2026 · Human Beta version beta-privacy-2026-09-02-v2
      </p>

      <div className="rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
        <section className={sectionClass}>
          <h2 className={headingClass}>1. Data controller and contact</h2>
          <p className={textClass}>
            Pinngro Inc. is the service operator and data controller for the Human Beta.
            Pinngro Inc. is a Delaware corporation formed on August 3, 2026, Delaware
            State File Number 10720305. Its registered office is 16192 Coastal Highway,
            Lewes, Delaware 19958, Sussex County, United States. Its registered agent is
            Harvard Business Services, Inc. Its principal business and correspondence
            address is 144 Triq Il-Bazilika, Mosta MST 1017, Malta.
          </p>
          <p className={textClass}>
            Privacy requests may be sent to
            <a className="ml-1 text-blue-600 hover:underline" href="mailto:privacy@pinngro.com">privacy@pinngro.com</a>.
            General support is available at
            <a className="ml-1 text-blue-600 hover:underline" href="mailto:support@pinngro.com">support@pinngro.com</a>.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>2. Scope of this policy</h2>
          <p className={textClass}>
            This policy covers the invitation-only Pinngro Human Beta, including account
            admission, Today, Stock Focus, factory and custom strategies, virtual Practice,
            historical Backtesting, Premium Backtesting, support, transactional email,
            Stripe Test Mode billing, and optional supported broker OAuth connectivity.
            Paper and Live broker-order submission are not enabled in this release.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>3. Information we process</h2>
          <ul className={listClass}>
            <li><strong>Invitation and account information:</strong> invitation status, username, email address, country, telephone number, verification state, policy acceptance, account status, and security/session version.</li>
            <li><strong>Authentication and security information:</strong> password hashes, two-factor authentication material, token digests, sign-in and security events, privacy-safe abuse-control digests, source network address, and administrative audit events.</li>
            <li><strong>Product content:</strong> Stock Focus selections, strategy definitions, bot settings, Backtests, Practice balances and positions, evaluations, proposed actions, approvals or rejections, virtual intents and fills, reports, and immutable evidence needed to explain outcomes.</li>
            <li><strong>Optional Ask Pinngro questions:</strong> when you choose to use Ask Pinngro, we process your question and the bounded recorded trade context needed to answer it. Ask Pinngro is optional and does not give our AI service provider access to your Pinngro account or database.</li>
            <li><strong>Test billing information:</strong> Stripe Test Mode customer, Price, subscription, event, period, and entitlement identifiers. Pinngro does not receive or store card details.</li>
            <li><strong>Email information:</strong> recipient address, message purpose, encrypted delivery secret while required, delivery attempts, and sanitized outcome codes for invitations, verification, and recovery.</li>
            <li><strong>Broker connectivity information:</strong> if you connect a supported broker, encrypted OAuth authorization, broker account environment and metadata, and supported account or market-data reads. Connectivity does not grant Pinngro automatic execution authority.</li>
            <li><strong>Operational information:</strong> application requests, errors, service health, timestamps, device/browser information ordinarily contained in HTTP requests, and security or support records.</li>
          </ul>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>4. How information is obtained</h2>
          <p className={textClass}>
            We receive information from you during invitation acceptance, registration,
            profile changes, strategy and bot use, support, and explicit decisions in
            Today. We create operational and immutable evidence as the service runs. We
            receive signed Test Mode billing events from Stripe, SMTP delivery outcomes
            from Mailgun, and authorized information from a broker only after you complete
            its OAuth flow.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>5. Why we process information</h2>
          <ul className={listClass}>
            <li>To admit invited users, create and secure accounts, verify email, recover access, and enforce suspension or session revocation.</li>
            <li>To provide Stock Focus, strategy tools, virtual Practice, Backtesting, Today decisions, reports, and customer support.</li>
            <li>When you choose Ask Pinngro, to generate an explanatory response about the recorded trade context you selected. Those responses do not replace Pinngro's recorded trading evidence and are not investment advice.</li>
            <li>To verify Stripe Test Mode subscriptions and provide Premium Backtesting when authoritative entitlement exists.</li>
            <li>To deliver necessary transactional messages through Mailgun SMTP.</li>
            <li>To prevent abuse, investigate errors and security events, preserve auditability, enforce tenant isolation, and meet legal obligations.</li>
            <li>To maintain and improve the reliability and usability of the Human Beta using operational information generated by the service itself.</li>
          </ul>
          <p className={textClass}>
            Depending on the processing, the applicable basis may be performance of our
            agreement with you, steps requested before entering that agreement, compliance
            with legal obligations, your consent where specifically requested, or our
            legitimate interests in providing and securing the service without overriding
            your rights.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>6. Browser storage, cookies, and tracking</h2>
          <p className={textClass}>
            The Pinngro application uses browser local storage for authentication state and
            display preferences, and session storage for short-lived setup drafts. Protect
            access to your device and sign out on shared devices. This Human Beta does not
            implement Google Analytics, advertising analytics, advertising cookies, or a
            cookie-consent platform. Pinngro does not intentionally set analytics or
            advertising cookies. Stripe-hosted or other third-party pages may use their own
            necessary technologies under their own policies.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>7. Service providers and disclosures</h2>
          <ul className={listClass}>
            <li><strong>Hetzner:</strong> hosts the current production infrastructure.</li>
            <li><strong>Mailgun:</strong> delivers invitation, verification, recovery, and other authorized transactional messages through authenticated STARTTLS SMTP.</li>
            <li><strong>Stripe:</strong> hosts Test Mode Checkout and the Test Mode customer portal and sends signed Test Mode billing events. Test cards do not create real charges.</li>
            <li><strong>Alpaca:</strong> provides user-authorized OAuth connectivity for supported account or market-data functions; no Paper or Live order submission is enabled.</li>
            <li><strong>OpenAI:</strong> provides the optional Ask Pinngro API. Only after you submit a question, it receives that question and the bounded recorded trade context needed to generate a response; it does not receive access to your Pinngro account or database.</li>
            <li><strong>Market and news data providers:</strong> supply information used by screening, strategy, and reporting features.</li>
          </ul>
          <p className={textClass}>
            We disclose only information reasonably necessary for the relevant service,
            security, support, legal requirement, or corporate transaction. We do not sell
            personal information or use it for third-party advertising.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>8. International processing</h2>
          <p className={textClass}>
            Pinngro operates from Malta through a Delaware corporation and uses providers
            that may process information in the European Economic Area, United States, or
            other locations where they operate. Where applicable law requires it, Pinngro
            uses an available lawful transfer mechanism or another recognized safeguard.
            Contact the privacy address for information relevant to your request.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>9. Retention and deletion</h2>
          <p className={textClass}>
            We retain information for the period needed to operate and secure the service,
            provide support, comply with law, resolve disputes, and preserve authoritative
            billing, security, strategy, and virtual Practice evidence. Raw invitation,
            verification, recovery, claim, and lease secrets are not retained as public
            evidence; the implementation uses digests, encryption, expiry, or deletion as
            appropriate to their function.
          </p>
          <p className={textClass}>
            Self-service account deletion is disabled during the Human Beta. Closure and
            erasure requests are support-controlled while Pinngro completes the approved
            workflow for active test subscriptions, statutory retention, security, and
            immutable evidence. We will not promise deletion of records that must lawfully
            or necessarily remain, but we will assess each request and explain the result.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>10. Security</h2>
          <p className={textClass}>
            Implemented controls include TLS for public traffic, password hashing,
            two-factor authentication, versioned encryption for sensitive application
            credentials, tenant-scoped authorization, database-backed session revocation,
            invitation and action-token digests, shared abuse controls, restricted
            production secrets, sanitized logging, signed Stripe webhooks, and durable
            idempotent processing. No system is perfectly secure; contact us promptly if
            you believe your account or information is at risk.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>11. Your choices and rights</h2>
          <p className={textClass}>
            Subject to applicable law, you may request access, correction, deletion,
            restriction, objection, or portability and may withdraw consent where consent
            is the basis for processing. You may update supported profile details in the
            application, disconnect broker OAuth access, cancel a Test Mode subscription
            through the hosted portal, or contact us. We may need to verify your identity
            and may retain information where law or the integrity of immutable security,
            billing, or Practice evidence requires it. You may also complain to an
            appropriate data-protection authority.
          </p>
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>12. Children</h2>
          <p className={textClass}>
            The Human Beta is intended only for adults who are at least 18 or the legal age
            of majority where they live. We do not knowingly invite children.
          </p>
        </section>

        <section>
          <h2 className={headingClass}>13. Changes to this policy</h2>
          <p className={textClass}>
            We may update this policy when the Human Beta, our providers, law, or processing
            changes. Material changes receive a new version identifier and renewed
            acceptance where appropriate. The version and date displayed above identify
            the policy presented to you.
          </p>
        </section>
      </div>
    </main>
  </div>
);

export default Privacy;
